import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '@/context/AuthContext';
import { KeyRound, ArrowLeft, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setMsg('');
    if (!username.trim()) { setErr('Please enter your username.'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API_BASE}/auth/forgot-password`, { username: username.trim() });
      setMsg(res.data.message);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to submit request.');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 text-slate-100">
      <div className="w-full max-w-md bg-slate-900/70 border border-slate-800 rounded-2xl p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-teal-500/10 border border-teal-500/20 text-teal-400 flex items-center justify-center">
            <KeyRound className="w-5 h-5" />
          </div>
          <div>
            <h1 data-testid="forgot-password-title" className="text-xl font-bold text-white">Reset Password</h1>
            <p className="text-xs text-slate-400 mt-0.5">Enter your username and we'll notify your administrator.</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">Username</label>
            <input
              data-testid="forgot-username-input"
              type="text"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Your username"
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500"
            />
          </div>
          {err && (
            <div data-testid="forgot-error" className="p-2.5 bg-red-950/60 border border-red-800/70 rounded-xl text-red-200 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {err}
            </div>
          )}
          {msg && (
            <div data-testid="forgot-message" className="p-2.5 bg-emerald-950/60 border border-emerald-800/60 rounded-xl text-emerald-200 text-xs flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{msg}</span>
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="forgot-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-slate-950 font-semibold text-sm rounded-xl transition-all"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Send Reset Instructions
          </button>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="w-full text-xs text-slate-400 hover:text-white flex items-center justify-center gap-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Login
          </button>
        </form>
      </div>
    </div>
  );
}
